import { useEffect, useRef, useState } from "react";
import { Facebook, Instagram, Loader2, Send, CalendarClock, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const PLATFORMS = [
  { key: "facebook", label: "Facebook", icon: Facebook, color: "#1877F2" },
  { key: "instagram", label: "Instagram", icon: Instagram, color: "#E1306C" },
];

const pad = (n) => String(n).padStart(2, "0");
export const toLocalInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

export const PlatformToggles = ({ value, onChange, hasImages, prefix = "platform" }) => (
  <div className="flex gap-2">
    {PLATFORMS.map((p) => {
      const on = value.includes(p.key);
      const disabled = p.key === "instagram" && !hasImages;
      return (
        <button
          key={p.key}
          type="button"
          disabled={disabled}
          title={disabled ? "Instagram wajib menyertakan gambar atau video" : ""}
          onClick={() => onChange(on ? value.filter((x) => x !== p.key) : [...value, p.key])}
          data-testid={`${prefix}-${p.key}-toggle`}
          style={on ? { background: p.color, borderColor: p.color } : {}}
          className={`flex items-center gap-1.5 px-3 h-9 rounded-xl border text-xs font-medium transition-colors disabled:opacity-40 ${on ? "text-white" : "border-[#E2E8F0] bg-[#F1F5F9] text-slate-700 hover:bg-[#E2E8F0]"}`}
        >
          {on ? <Check size={13} /> : <p.icon size={13} />} {p.label}
        </button>
      );
    })}
  </div>
);

export const SocialPublishActions = ({ buildText, images, videoUrl = "", coverUrl = "", businessId, canPost = true, disabled = false, onBusyChange = () => {} }) => {
  const [targets, setTargets] = useState(["facebook"]);
  const [title, setTitle] = useState("");
  const [scheduleMode, setScheduleMode] = useState(false);
  const [when, setWhen] = useState(() => toLocalInput(new Date(Date.now() + 60 * 60 * 1000)));
  const [busy, setBusy] = useState("");
  const mounted = useRef(true);
  const polling = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const changeBusy = (value) => { setBusy(value); onBusyChange(!!value); };
  const hasMedia = images.length > 0 || !!videoUrl;
  const effTargets = hasMedia ? targets : targets.filter((t) => t !== "instagram");
  const label = (t) => (t === "facebook" ? (videoUrl ? "Facebook Video" : "Facebook") : (videoUrl ? "Instagram Reels" : "Instagram"));
  const summarize = (results) => effTargets.map((t) => {
    const res = results?.[t];
    if (res?.ok) return res.warning ? `${label(t)} ✓ (${res.warning})` : `${label(t)} ✓`;
    return `${label(t)} ✗ (${res?.error || "gagal"})`;
  }).join(" · ");

  const pollVideo = async (id) => {
    polling.current = true;
    const tid = toast.loading("Video sedang diproses Meta… (bisa beberapa menit)");
    for (let i = 0; i < 90 && mounted.current; i += 1) {
      await new Promise((r) => setTimeout(r, 5000));
      if (!mounted.current) { toast.dismiss(tid); polling.current = false; return; }
      try {
        const d = (await api.get(`/social/posts/${id}`)).data;
        if (d.status === "done") { toast.success(`Video berhasil diposting: ${summarize(d.results)}`, { id: tid }); polling.current = false; changeBusy(""); return; }
        if (d.status === "failed") { toast.error(`Posting video gagal: ${summarize(d.results)}`, { id: tid, duration: 10000 }); polling.current = false; changeBusy(""); return; }
      } catch { /* coba lagi */ }
    }
    toast.message("Video masih diproses — cek hasilnya di Konten Terjadwal → Riwayat Posting", { id: tid });
    polling.current = false;
    if (mounted.current) changeBusy("");
  };

  const prepare = () => {
    if (!businessId) { toast.error("Pilih bisnis terlebih dahulu"); return null; }
    if (!effTargets.length) { toast.error("Pilih minimal satu platform"); return null; }
    return buildText(effTargets);
  };

  const postNow = async () => {
    const text = prepare();
    if (!text) return;
    changeBusy("now");
    try {
      const r = await api.post("/social/publish", { text, title, image_urls: videoUrl ? [] : images, video_url: videoUrl, cover_url: coverUrl, targets: effTargets, business_id: businessId });
      if (r.data.processing) { pollVideo(r.data.history_id); return; }
      if (r.data.ok) toast.success(`Berhasil posting: ${summarize(r.data.results)}`);
      else toast.error(`Sebagian gagal: ${summarize(r.data.results)}`);
    } catch (e) { toast.error(apiError(e)); } finally { if (!polling.current) changeBusy(""); }
  };

  const saveSchedule = async () => {
    const text = prepare();
    if (!text) return;
    const dt = new Date(when);
    if (!when || isNaN(dt.getTime())) { toast.error("Isi tanggal & waktu posting"); return; }
    if (dt <= new Date()) { toast.error("Waktu posting harus di masa depan"); return; }
    changeBusy("schedule");
    try {
      await api.post("/scheduled-posts", {
        business_id: businessId, title, text, image_urls: videoUrl ? [] : images, video_url: videoUrl, cover_url: coverUrl, auto_social: effTargets,
        channel: effTargets.join("+"), send_at: dt.toISOString(),
      });
      toast.success(`Konten terjadwal ${dt.toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })} — lihat di menu Konten Terjadwal`);
      setScheduleMode(false);
      setTitle("");
    } catch (e) { toast.error(apiError(e)); } finally { changeBusy(""); }
  };

  if (!canPost) return <p className="text-[11px] text-slate-500" data-testid="social-publish-readonly">Posting &amp; penjadwalan hanya untuk Admin Bisnis / Super Admin.</p>;

  return (
    <div className="space-y-3" data-testid="social-publish-actions">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-600">Judul konten</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Contoh: Promo Akhir Pekan" data-testid="social-title-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1 h-9 text-sm" />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Platform tujuan</Label>
          <div className="mt-1"><PlatformToggles value={effTargets} onChange={setTargets} hasImages={hasMedia} /></div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={postNow} disabled={!!busy || disabled} data-testid="post-now-button" className="flex-1 min-w-[150px] bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl text-xs">
          {busy === "now" ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />} Posting Sekarang
        </Button>
        <Button onClick={() => { if (!scheduleMode) setWhen(toLocalInput(new Date(Date.now() + 60 * 60 * 1000))); setScheduleMode((v) => !v); }} disabled={!!busy || disabled} variant="outline" data-testid="schedule-toggle-button" className={`flex-1 min-w-[150px] rounded-xl text-xs ${scheduleMode ? "border-[#D97706] bg-[#D97706]/10 text-[#B45309]" : "border-[#E2E8F0] bg-[#F1F5F9] hover:bg-[#E2E8F0]"}`}>
          <CalendarClock size={14} className="mr-1" /> Jadwalkan
        </Button>
      </div>
      {scheduleMode && (
        <div className="flex flex-wrap items-end gap-2 bg-[#F1F5F9] border border-[#FDE68A] rounded-xl p-3" data-testid="schedule-form">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs text-slate-600">Tanggal &amp; waktu posting</Label>
            <Input type="datetime-local" value={when} min={toLocalInput(new Date())} onChange={(e) => setWhen(e.target.value)} data-testid="schedule-datetime-input" className="bg-[#FFFFFF] border-[#E2E8F0] mt-1 h-9 text-sm" />
          </div>
          <Button onClick={saveSchedule} disabled={!!busy || disabled} data-testid="schedule-submit-button" className="bg-[#D97706] hover:bg-[#F59E0B] rounded-xl text-xs h-9">
            {busy === "schedule" ? <Loader2 size={14} className="animate-spin mr-1" /> : <CalendarClock size={14} className="mr-1" />} Simpan Jadwal
          </Button>
          <p className="w-full text-[10px] text-slate-500">Sistem memposting otomatis saat waktunya tiba (dicek tiap ±30 detik selama backend aktif).</p>
        </div>
      )}
    </div>
  );
};
